import { useContext, useState, useEffect } from "react";
import { css } from "@emotion/react";
import {
  useMeLazyQuery,
  useSyncPasswordToAegisMutation,
  useResolveIdpLazyQuery,
  UserSyncStatus,
} from "@/generated/graphql";
import {
  ForgotPasswordLink,
  BizsolLoginButton,
  BizsolRedirectLoginLink,
  ConnectBoardLoginButton,
  ConnectBoardTopLink,
} from "@/components/atoms";
import { Font, useSnackBar } from "@moneyforward/mfx_biz_react_components";
import { PageTitle } from "@/components/molecules";
import { SignInForm } from "@/components/organisms";
import { MainTemplate } from "@/components/templates";
import { ProvisionalRegistrationLink } from "@/components/atoms/ProvisionalRegistrationLink";
import { useIsSpMode } from "@/hooks";
import { useSignInError } from "@/hooks/useSignInError";
import { AuthContext } from "@/contexts/AuthContext";
import { SignInErrorBanner } from "@/components/atoms/SignInErrorBanner";
import config from "@/libs/config";
import * as constants from "@/libs/constants";
import { useRouter } from "next/router";
import { QUERY_PARAM_APP } from "@/libs/queryParams";

type FormValues = {
  username: string;
  password: string;
};

type Props = {
  few?: boolean;
};

export const SignInPage = (props: Props) => {
  const isSpMode = useIsSpMode();
  const authContext = useContext(AuthContext);
  const { notify } = useSnackBar();
  const { hasError, showError, clearError } = useSignInError();
  const [step, setStep] = useState<"username" | "password">("username");
  const [meQuery] = useMeLazyQuery();
  const [syncPasswordToAegis] = useSyncPasswordToAegisMutation();
  const [resolveIdp, { loading: isResolvingIdp }] = useResolveIdpLazyQuery();
  // default value is DXF, if we add blank string then it cause issue as app will be undefine for without value
  const [appParamServiceName, setAppParamServiceName] = useState<string>(
    constants.ServiceNames.DXF.NameJaJp
  );
  const router = useRouter();

  // === Side effects ===
  useEffect(() => {
    if (router.isReady) {
      const { app } = router.query;
      if (typeof app === "string") {
        // change text only for invoice
        if (app === QUERY_PARAM_APP.invoice) {
          setAppParamServiceName(constants.ServiceNames.Invoice.NameJaJp);
        }
      }
    }
  }, [router.isReady, router.query]);

  // === Flags for conditional rendering ===
  const displayProvisionalRegistrationLink = !props.few && !config.USING_BIZSOL;
  const displayBizsolButton = config.USING_BIZSOL && config.IS_SHIKOKUBANK;
  const displayBizsolRedirectLoginLink =
    config.USING_BIZSOL && config.IS_KYOSHIN;
  const displayConnectBoardTopLink = config.USING_CONNBOARD;
  const displayConnectBoardLoginButton = config.USING_CONNBOARD;
  const hideSignInForm = config.USING_CONNBOARD; // ConnectBoardの場合はログインフォームを非表示にする
  const displayTwoStepForm = config.USING_AEGIS;

  // === Handlers ===
  const handleSubmit = async (formValues: FormValues) => {
    clearError();
    authContext
      .signIn(formValues.username, formValues.password)
      .then(async (succeeded: boolean) => {
        if (succeeded) {
          const meResult = await meQuery();
          const migration = meResult.data?.me?.migrationStatus;

          if (
            migration?.syncStatus === UserSyncStatus.Success &&
            !migration.passwordSync
          ) {
            // Silently sync password to KC on first login after migration.
            // Session continues normally — next login resolveIdp routes to KC.
            await syncPasswordToAegis({
              variables: { input: { password: formValues.password } },
            });
          }

          notify("ログインが完了しました");
        }
      })
      .catch(() => {
        if (config.USING_AEGIS) {
          showError();
        } else {
          notify("ログインに失敗しました");
        }
      });
  };

  const handleNext = async (username: string): Promise<boolean> => {
    const { client_id, redirect_uri, response_type, token, redirectUrl } =
      router.query;

    // Embed token + redirectUrl into state so they survive the Keycloak round-trip.
    // The backend stores this as original_state in aegisStatePayload and returns it
    // after the code exchange, allowing the callback to restore the redirect.
    const enrichedState = JSON.stringify({
      token: typeof token === "string" ? token : undefined,
      redirectUrl: typeof redirectUrl === "string" ? redirectUrl : undefined,
    });

    const result = await resolveIdp({
      variables: {
        username,
        clientId:
          typeof client_id === "string" ? client_id : config.COGNITO_CLIENT_ID,
        redirectUri:
          typeof redirect_uri === "string" ? redirect_uri : config.XID_URL,
        state: enrichedState,
        responseType:
          typeof response_type === "string" ? response_type : "code",
      },
    });
    if (result.data?.resolveIdp) {
      router.push(result.data.resolveIdp);
      return true; // Aegis → redirecting, don't show password step
    }
    return false; // Cognito → show password step
  };

  // === Render ===
  return (
    <MainTemplate title="ログイン" disableAccountDropDown={true}>
      {config.USING_AEGIS && hasError && (
        <SignInErrorBanner
          onDismiss={() => {
            clearError();
            setStep("username");
          }}
        />
      )}
      <PageTitle
        mainTitle={`ログイン`}
        subTitle={`${config.BANK_NICKNAME} Mikatano シリーズの各サービスを共通のIDで利用できます`}
      />
      {config.IS_SHIKOKUBANK && (
        <div css={closedRegistrationNotice}>
          <Font variant="m">
            Mikatanoの新規登録の受付は終了いたしました。
            <br />
            すでにアカウントをお持ちのお客さまは、
            <br />
            登録済みのログイン情報にて引き続きご利用いただけます。
          </Font>
        </div>
      )}
      {displayBizsolButton && (
        <>
          <BizsolLoginButton />
          <div css={subTextWrapper}>
            ※{config.BIZSOL_DISPLAY_NAME}
            をご利用の方はこちらからログインを行ってください
          </div>
        </>
      )}
      {displayConnectBoardLoginButton && (
        <>
          <ConnectBoardLoginButton />
        </>
      )}
      {displayConnectBoardTopLink && (
        <>
          <ConnectBoardTopLink />
        </>
      )}
      {hideSignInForm ? null : (
        <>
          <SignInForm
            step={step}
            onStepChange={setStep}
            onSubmit={handleSubmit}
            onNext={displayTwoStepForm ? handleNext : undefined}
            isResolvingIdp={isResolvingIdp}
          />
          <div css={!isSpMode ? linkList : spModeLinkList}>
            {displayBizsolRedirectLoginLink && (
              <div css={linkItem}>
                <BizsolRedirectLoginLink />
              </div>
            )}
            <div css={linkItem}>
              <ForgotPasswordLink />
            </div>
            {displayProvisionalRegistrationLink ? (
              <div>
                {config.IS_KYOTOBANK ||
                config.IS_SHIGAGIN ? null : config.IS_CHUGIN ? (
                  <div css={chuginProvisionalRegistrationLink}>
                    <ProvisionalRegistrationLink
                      app="bfm"
                      text="Mikatano 資金管理を新規登録するにはこちらへ"
                    />
                    <p>
                      Mikatano {appParamServiceName}
                      をご利用いただくには先にMikatano資金管理の登録が必要です。
                      <br />
                      既にMikatano
                      資金管理をご利用中の方は、上記ログインから申込みが可能です。
                    </p>
                  </div>
                ) : (
                  <ProvisionalRegistrationLink />
                )}
              </div>
            ) : null}
          </div>
        </>
      )}
    </MainTemplate>
  );
};

const linkList = css({
  marginTop: "60px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const spModeLinkList = css({
  marginTop: "23px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
});

const linkItem = css({
  marginBottom: "15px",
});

const subTextWrapper = css({
  display: "flex",
  justifyContent: "center",
  marginBottom: "48px",
});

const closedRegistrationNotice = css({
  textAlign: "center",
  marginBottom: "32px",
});

const chuginProvisionalRegistrationLink = css({
  textAlign: "center",
});
